'use client';
import React from 'react';
import { useForm } from 'react-hook-form';
import { ArrowRight, ChevronLeft } from 'lucide-react';
import type { WizardData } from './VisaWizard';

interface Props {
  data: WizardData;
  updateData: (d: Partial<WizardData>) => void;
  onNext: () => void;
  onBack: () => void;
}

const nationalities = [
  'Pakistani', 'Indian', 'Bangladeshi', 'Moroccan', 'Egyptian', 'Nigerian',
  'Chinese', 'Filipino','Brazilian', 'Iranian', 'Turkish', 'Russian', 'Ukrainian', 'Saudi Arabian',
  'Emirati', 'Jordanian', 'Lebanese', 'Syrian', 'Iraqi', 'Afghani',
  'Sri Lankan', 'Nepali', 'Indonesian', 'Malaysian', 'Vietnamese', 'Thai',
  'Ghanaian', 'Kenyan', 'Ethiopian', 'South African', 'Algerian', 'Tunisian',
];

const durations = [
  { value: '1-7', label: '1–7 days', sub: 'Short trip' },
  { value: '8-14', label: '8–14 days', sub: 'Two weeks' },
  { value: '15-30', label: '15–30 days', sub: 'One month' },
  { value: '31-90', label: '31–90 days', sub: 'Up to 3 months' },
  { value: '91-180', label: '91–180 days', sub: 'Up to 6 months' },
  { value: '180+', label: '180+ days', sub: 'Long stay' },
];

const sponsorTypes = [
  { value: 'self', label: 'Self-sponsored', sub: 'I am funding my own trip' },
  { value: 'employer', label: 'Employer / company', sub: 'My employer is sponsoring me' },
  { value: 'family', label: 'Family member', sub: 'A relative is sponsoring me' },
  { value: 'institution', label: 'University / institution', sub: 'An institution is sponsoring me' },
];

interface FormValues {
  fullName: string;
  nationality: string;
  passportNumber: string;
  travelDate: string;
  travelDuration: string;
  sponsorType: string;
  hasTravelHistory: boolean;
  travelHistoryDetails: string;
}

export default function Step3PersonalInfo({ data, updateData, onNext, onBack }: Props) {
  const { register, handleSubmit, watch, formState: { errors } } = useForm<FormValues>({
    defaultValues: {
      fullName: data.fullName,
      nationality: data.nationality,
      passportNumber: data.passportNumber,
      travelDate: data.travelDate,
      travelDuration: data.travelDuration || '15-30',
      sponsorType: data.sponsorType || 'self',
      hasTravelHistory: data.hasTravelHistory,
      travelHistoryDetails: data.travelHistoryDetails,
    },
  });

  const hasTravelHistory = watch('hasTravelHistory');
  const selectedDuration = watch('travelDuration');
  const selectedSponsor = watch('sponsorType');

  const onSubmit = (values: FormValues) => {
    updateData(values);
    onNext();
  };

  return (
    <div className="wizard-card space-y-7 animate-slide-up">
      {/* Header */}
      <div className="space-y-1">
        <p className="text-xs font-semibold text-blue-600 uppercase tracking-widest">Step 3 of 9</p>
        <h2 className="text-xl font-extrabold text-gray-900 tracking-tight">Tell us about yourself</h2>
        <p className="text-sm text-gray-500">
          We use this to tailor your visa requirements and generate your application documents.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Full name */}
        <div className="space-y-1.5">
          <label htmlFor="fullName" className="text-sm font-bold text-gray-700">
            Full name <span className="text-red-400">*</span>
          </label>
          <p className="text-xs text-gray-400">As it appears on your passport</p>
          <input
            id="fullName"
            type="text"
            placeholder="e.g. Layla Mohammed Al-Rashidi"
            className="input-field"
            {...register('fullName', { required: 'Full name is required' })}
          />
          {errors.fullName && <p className="text-xs text-red-500 font-medium">{errors.fullName.message}</p>}
        </div>

        {/* Nationality */}
        <div className="space-y-1.5">
          <label htmlFor="nationality" className="text-sm font-bold text-gray-700">
            Nationality <span className="text-red-400">*</span>
          </label>
          <p className="text-xs text-gray-400">Your country of citizenship (not residence)</p>
          <select
            id="nationality"
            className="input-field bg-white"
            {...register('nationality', { required: 'Nationality is required' })}
          >
            <option value="">Select your nationality</option>
            {nationalities.map((nat) => (
              <option key={`nat-${nat}`} value={nat}>{nat}</option>
            ))}
          </select>
          {errors.nationality && <p className="text-xs text-red-500 font-medium">{errors.nationality.message}</p>}
        </div>

        {/* Passport number */}
        <div className="space-y-1.5">
          <label htmlFor="passportNumber" className="text-sm font-bold text-gray-700">
            Passport number <span className="text-red-400">*</span>
          </label>
          <p className="text-xs text-gray-400">Used only for document generation — stored encrypted</p>
          <input
            id="passportNumber"
            type="text"
            placeholder="e.g. AB1234567"
            className="input-field font-mono tracking-widest uppercase"
            {...register('passportNumber', {
              required: 'Passport number is required',
              minLength: { value: 6, message: 'Passport number must be at least 6 characters' },
            })}
          />
          {errors.passportNumber && <p className="text-xs text-red-500 font-medium">{errors.passportNumber.message}</p>}
        </div>

        {/* Travel date */}
        <div className="space-y-1.5">
          <label htmlFor="travelDate" className="text-sm font-bold text-gray-700">
            Planned travel date <span className="text-red-400">*</span>
          </label>
          <p className="text-xs text-gray-400">Approximate is fine — used to estimate processing urgency</p>
          <input
            id="travelDate"
            type="date"
            className="input-field"
            {...register('travelDate', { required: 'Travel date is required' })}
          />
          {errors.travelDate && <p className="text-xs text-red-500 font-medium">{errors.travelDate.message}</p>}
        </div>

        {/* Travel duration */}
        <div className="space-y-2">
          <label className="text-sm font-bold text-gray-700">
            How long will you stay? <span className="text-red-400">*</span>
          </label>
          <div className="grid grid-cols-3 gap-2">
            {durations.map((d) => (
              <label
                key={`dur-${d.value}`}
                className={`flex flex-col items-center gap-0.5 p-2.5 rounded-xl border cursor-pointer text-center transition-all duration-150 ${
                  selectedDuration === d.value
                    ? 'border-blue-300 bg-blue-50 ring-2 ring-blue-100' :'border-gray-200 bg-white hover:border-blue-200'
                }`}
              >
                <input type="radio" value={d.value} className="sr-only" {...register('travelDuration', { required: true })} />
                <span className={`text-xs font-bold ${selectedDuration === d.value ? 'text-blue-700' : 'text-gray-700'}`}>{d.label}</span>
                <span className="text-xs text-gray-400">{d.sub}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Sponsor type */}
        <div className="space-y-2">
          <label className="text-sm font-bold text-gray-700">Who is sponsoring your trip?</label>
          <div className="grid sm:grid-cols-2 gap-2">
            {sponsorTypes.map((s) => (
              <label
                key={`sponsor-${s.value}`}
                className={`flex items-center gap-2.5 p-3 rounded-xl border cursor-pointer transition-all duration-150 ${
                  selectedSponsor === s.value
                    ? 'border-blue-300 bg-blue-50 ring-2 ring-blue-100' :'border-gray-200 bg-white hover:border-blue-200'
                }`}
              >
                <input type="radio" value={s.value} className="sr-only" {...register('sponsorType')} />
                <div className={`w-3 h-3 rounded-full border-2 shrink-0 ${
                  selectedSponsor === s.value ? 'border-blue-600 bg-blue-600' : 'border-gray-300'
                }`} />
                <div>
                  <p className={`text-xs font-bold ${selectedSponsor === s.value ? 'text-blue-800' : 'text-gray-700'}`}>{s.label}</p>
                  <p className="text-xs text-gray-400">{s.sub}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Travel history */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-bold text-gray-700">Do you have prior travel history?</label>
              <p className="text-xs text-gray-400 mt-0.5">Previous visas or stamps can strengthen your application</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" {...register('hasTravelHistory')} />
              <div className="w-10 h-5 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 transition-colors duration-200" />
              <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 peer-checked:translate-x-5" />
            </label>
          </div>

          {hasTravelHistory && (
            <div className="space-y-1.5 animate-fade-in">
              <label htmlFor="travelHistoryDetails" className="text-sm font-semibold text-gray-700">
                List countries visited (optional)
              </label>
              <p className="text-xs text-gray-400">e.g. UAE (2023), Turkey (2024), Malaysia (2025)</p>
              <textarea
                id="travelHistoryDetails"
                rows={3}
                placeholder="UAE (2023), Turkey (2024)..."
                className="input-field resize-none"
                {...register('travelHistoryDetails')}
              />
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex items-center gap-3 pt-2">
          <button type="button" onClick={onBack} className="btn-secondary py-2.5 px-4 text-sm">
            <ChevronLeft size={15} />
            Back
          </button>
          <button type="submit" className="btn-primary flex-1 justify-center py-2.5 text-sm">
            Generate my requirements
            <ArrowRight size={15} />
          </button>
        </div>
      </form>
    </div>
  );
}